import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { EmptyState, EmptyStateTitle } from '@/components/EmptyState'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { type Id } from '@/infra/id'
import styles from './JobProgressionHistoryModal.module.css'

export interface JobProgressionHistoryModalActor extends AvatarSubject {
  displayName: string
  roleLabel?: string
}

export interface JobProgressionHistoryModalItem {
  id: Id
  title: string
  formattedDateCreated: string | null
  createdBy: JobProgressionHistoryModalActor | null
}

export interface JobProgressionHistoryModalProps {
  showing: boolean
  items: JobProgressionHistoryModalItem[]
  onClose: () => unknown
}

export function JobProgressionHistoryModal({
  showing,
  items,
  onClose,
}: JobProgressionHistoryModalProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      testId="job-progression-history-modal"
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Progression history</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        {items.length === 0 ? (
          <EmptyState pad>
            <EmptyStateTitle>
              No progression history for this job yet.
            </EmptyStateTitle>
          </EmptyState>
        ) : (
          items.map((item) => (
            <ModalSheetSection spaced bordered key={item.id}>
              <HistoryStep item={item} />
            </ModalSheetSection>
          ))
        )}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" onPress={onClose} variant="default" />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface HistoryStepProps {
  item: JobProgressionHistoryModalItem
}

function HistoryStep({ item }: HistoryStepProps) {
  return (
    <div className={styles.step} data-test="job-progression-history-step">
      <div className={styles.stepTitle}>{item.title}</div>
      <div className={styles.entry}>
        {item.formattedDateCreated && (
          <span className={styles.time}>{item.formattedDateCreated}</span>
        )}
        <HistoryStepActor actor={item.createdBy} />
      </div>
    </div>
  )
}

interface HistoryStepActorProps {
  actor: JobProgressionHistoryModalActor | null
}

function HistoryStepActor({ actor }: HistoryStepActorProps) {
  if (!actor) {
    return <span className={styles.actor}>—</span>
  }

  return (
    <span className={styles.actorMember}>
      <Avatar subject={actor} size={20} />
      <span className={styles.actorName}>{actor.displayName}</span>
      {actor.roleLabel && (
        <span className={styles.role}>{actor.roleLabel}</span>
      )}
    </span>
  )
}
